package com.schoolconnect.app.feature.attendance

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.RowScope
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Icon
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.schoolconnect.app.designsystem.AppDialogActionStyle
import com.schoolconnect.app.designsystem.AppDialogBody
import com.schoolconnect.app.designsystem.AppDialogDateField
import com.schoolconnect.app.designsystem.AppDialogDefaultColors
import com.schoolconnect.app.designsystem.AppDialogFieldColumn
import com.schoolconnect.app.designsystem.AppDialogFieldRow
import com.schoolconnect.app.designsystem.AppDialogFooter
import com.schoolconnect.app.designsystem.AppDialogFooterAction
import com.schoolconnect.app.designsystem.AppDialogHeader
import com.schoolconnect.app.designsystem.AppDialogSearchSelectField
import com.schoolconnect.app.designsystem.AppDialogScaffold
import com.schoolconnect.app.designsystem.AppDialogSize
import com.schoolconnect.app.designsystem.AppDialogTextField
import com.schoolconnect.app.designsystem.AppDialogValidationSummary
import com.schoolconnect.app.designsystem.AppDialogErrorMessage
import com.schoolconnect.app.designsystem.AppDropdownMenu
import com.schoolconnect.app.designsystem.currentAppDateLabel
import com.schoolconnect.app.designsystem.list.DesktopAwareLazyColumn
import com.schoolconnect.app.feature.common.compareBySmartText
import com.schoolconnect.app.designsystem.AppInfoBg
import com.schoolconnect.app.designsystem.AppInfoBorder
import com.schoolconnect.app.designsystem.AppInfoText
import com.schoolconnect.app.designsystem.AppBorder
import com.schoolconnect.app.designsystem.AppDanger
import com.schoolconnect.app.designsystem.AppDangerSoft
import com.schoolconnect.app.designsystem.AppNavy
import com.schoolconnect.app.designsystem.AppOrangeSoft
import com.schoolconnect.app.designsystem.AppOrangeText
import com.schoolconnect.app.designsystem.AppPrimary
import com.schoolconnect.app.designsystem.AppPrimarySoft
import com.schoolconnect.app.designsystem.AppSuccess
import com.schoolconnect.app.designsystem.AppSuccessSoft
import com.schoolconnect.app.repository.systemlookup.SystemLookupRepository
import ischoolconnect.sharedui.generated.resources.Res
import ischoolconnect.sharedui.generated.resources.chevron_down
import ischoolconnect.sharedui.generated.resources.circle_x
import ischoolconnect.sharedui.generated.resources.info_circle
import ischoolconnect.sharedui.generated.resources.plus
import ischoolconnect.sharedui.generated.resources.radio_selected
import ischoolconnect.sharedui.generated.resources.radio_unselected
import ischoolconnect.sharedui.generated.resources.trash
import org.jetbrains.compose.resources.DrawableResource
import org.jetbrains.compose.resources.painterResource

private data class QuickMarkRow(
    val student: AttendanceRowUi,
    val status: String,
    val remark: String
)

private fun String.toQuickMarkStatus(): String? = when (trim().uppercase()) {
    "A", "ABSENT" -> "A"
    "A-M", "A_M", "ABSENT MORNING", "ABSENT_MORNING" -> "A-M"
    "A-A", "A_A", "ABSENT AFTERNOON", "ABSENT_AFTERNOON" -> "A-A"
    else -> null
}

private fun com.schoolconnect.app.datasource.network.response.LookupItemResponse.isQuickMarkStatusAllowed(): Boolean {
    val normalizedCode = code?.trim()?.uppercase().orEmpty()
    val normalizedLabel = displayValue?.trim()?.uppercase().orEmpty()
    return normalizedCode !in setOf("P", "L", "E", "PRESENT", "LATE", "EXCUSED") &&
        normalizedLabel !in setOf("P", "L", "E", "PRESENT", "LATE", "EXCUSED")
}

@Composable
internal fun QuickMarkAttendanceDialog(
    visible: Boolean,
    students: List<AttendanceRowUi>,
    classOptions: List<AttendanceClassOption>,
    initialClassSectionId: String? = null,
    initialDate: String? = null,
    isRosterLoading: Boolean = false,
    isAttendanceRecordsLoading: Boolean = false,
    rosterErrorMessage: String = "",
    onContextChanged: (String, String) -> Unit = { _, _ -> },
    isSaving: Boolean = false,
    errorMessage: String = "",
    onDismiss: () -> Unit,
    onSave: (QuickMarkSubmission) -> Unit
) {
    if (!visible) return

    val lookupSnapshot by SystemLookupRepository.snapshot.collectAsState()
    val attendanceStatusItems = lookupSnapshot.activeValues("ATTENDANCE_STATUS")
    val quickMarkStatusItems = attendanceStatusItems.filter { it.isQuickMarkStatusAllowed() }
    LaunchedEffect(Unit) {
        if (attendanceStatusItems.isEmpty()) {
            SystemLookupRepository.loadCategoryItems("ATTENDANCE_STATUS")
        }
    }

    // Match the page's selected class when opening Quick Mark. If class options
    // arrive asynchronously, the effect below applies the matching section.
    var selectedClass by remember(visible) { mutableStateOf<AttendanceClassOption?>(null) }
    var selectedDate by remember(initialDate) { mutableStateOf(initialDate?.takeIf { it.isNotBlank() } ?: currentAppDateLabel()) }
    var selectedStatus by remember { mutableStateOf("A") }
    var studentPickerQuery by remember { mutableStateOf("") }
    var studentPickerExpanded by remember { mutableStateOf(false) }
    var selectedStudentKey by remember { mutableStateOf<String?>(null) }
    var hasSubmitted by remember { mutableStateOf(false) }

    LaunchedEffect(visible, initialClassSectionId, classOptions) {
        if (!visible) return@LaunchedEffect
        val matchingClass = initialClassSectionId
            ?.takeIf(String::isNotBlank)
            ?.let { sectionId -> classOptions.firstOrNull { it.id == sectionId } }
        if (matchingClass != null && selectedClass?.id != matchingClass.id) {
            selectedClass = matchingClass
            selectedStudentKey = null
            studentPickerQuery = ""
            onContextChanged(matchingClass.id, selectedDate)
        }
    }

    val classError = if (hasSubmitted && selectedClass == null) "Class is required." else ""
    val dateError = if (hasSubmitted) attendanceDateValidationError(selectedDate, currentAppDateLabel()) else ""

    fun existingExceptionsFor(): Map<String, QuickMarkRow> =
        students.asSequence()
            .mapNotNull { student ->
                student.status.toQuickMarkStatus()?.let { status ->
                    student.key to QuickMarkRow(student = student, status = status, remark = student.remarks)
                }
            }
            .toMap()

    var entries by remember(selectedClass?.id, students) {
        mutableStateOf(existingExceptionsFor())
    }

    // Keep the roster in human numeric order (1, 2, …, 10), independent of API order.
    val classStudents = remember(students) {
        students.sortedWith(compareBySmartText { it.rollNumber })
    }
    val studentOptions = remember(classStudents, entries, studentPickerQuery) {
        classStudents.filter {
            it.key !in entries &&
                (studentPickerQuery.isBlank() || it.studentName.contains(studentPickerQuery, true) || it.rollNumber.contains(studentPickerQuery, true))
        }
    }
    val selectedStudent = remember(selectedStudentKey, classStudents) {
        selectedStudentKey?.let { key -> classStudents.firstOrNull { it.key == key } }
    }

    fun addStudent(student: AttendanceRowUi) {
        entries = entries + (student.key to QuickMarkRow(student, selectedStatus, ""))
        selectedStudentKey = null
        studentPickerQuery = ""
    }
    // Students omitted from the exception list are Present by default.
    val statusCounts = remember(classStudents, entries) {
        classStudents.fold(mutableMapOf("P" to 0, "A" to 0, "A-M" to 0, "A-A" to 0)) { counts, student ->
            val status = entries[student.key]?.status ?: "P"
            counts[status]?.let { counts[status] = it + 1 }
            counts
        }
    }
    val presentCount = statusCounts["P"] ?: 0
    val absentCount = statusCounts["A"] ?: 0
    val morningCount = statusCounts["A-M"] ?: 0
    val afternoonCount = statusCounts["A-A"] ?: 0

    AppDialogScaffold(onDismiss = { if (!isSaving) onDismiss() }, size = AppDialogSize.Wide, dialogWidthOverride = 1120.dp, maxDialogHeight = 900.dp) {
        AppDialogHeader(title = "Quick Mark Attendance", subtitle = "", onClose = { if (!isSaving) onDismiss() }, height = 56.dp)
        AppDialogValidationSummary(errors = listOf(classError, dateError), modifier = Modifier.padding(horizontal = 28.dp, vertical = 12.dp))
        AppDialogErrorMessage(message = errorMessage.ifBlank { rosterErrorMessage }, modifier = Modifier.padding(horizontal = 28.dp, vertical = 4.dp))
        AppDialogBody(horizontalPadding = 28.dp, verticalPadding = 0.dp) {
            AppDialogFieldRow(spacing = 24.dp) {
                QuickMarkClassDropdown(
                    value = selectedClass?.label.orEmpty(),
                    options = classOptions,
                    onValueChange = {
                        selectedClass = it
                        selectedStudentKey = null
                        studentPickerQuery = ""
                        entries = existingExceptionsFor()
                        onContextChanged(it.id, selectedDate)
                    },
                    modifier = Modifier.weight(.82f),
                    error = classError
                )
                AppDialogFieldColumn(label = "Date", modifier = Modifier.weight(1f)) {
                    AppDialogDateField(
                        value = selectedDate,
                        onValueChange = {
                            selectedDate = it
                            if (attendanceDateValidationError(it, currentAppDateLabel()).isBlank()) {
                                selectedClass?.let { option -> onContextChanged(option.id, it) }
                            }
                        },
                        modifier = Modifier.fillMaxWidth(),
                        colors = AppDialogDefaultColors,
                        fieldHeight = 60.dp,
                        fontSize = 16.sp,
                        error = dateError
                    )
                }
            }

            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(14.dp)) {
                QuickMarkStatCard("Total Students", if (isRosterLoading) "…" else classStudents.size.toString(), AppPrimary, AppPrimarySoft, Modifier.weight(1f))
                QuickMarkStatCard("Present (P)", if (isRosterLoading) "…" else presentCount.toString(), AppSuccess, AppSuccessSoft, Modifier.weight(1f))
                QuickMarkStatCard("Absent (A)", if (isRosterLoading) "…" else absentCount.toString(), AppDanger, AppDangerSoft, Modifier.weight(1f))
                QuickMarkStatCard("Absent AM (A-M)", if (isRosterLoading) "…" else morningCount.toString(), AppOrangeText, AppOrangeSoft, Modifier.weight(1f))
                QuickMarkStatCard("Absent PM (A-A)", if (isRosterLoading) "…" else afternoonCount.toString(), AppOrangeText, AppOrangeSoft, Modifier.weight(1f))
            }

            Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.Bottom, horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                Column(modifier = Modifier.weight(1.55f), verticalArrangement = Arrangement.spacedBy(7.dp)) {
                    Text("Attendance Status", color = AppNavy, fontSize = 14.sp, fontWeight = FontWeight.ExtraBold)
                    Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                        quickMarkStatusItems.forEach { item ->
                            val code = item.code.orEmpty()
                            val label = item.displayValue.orEmpty()
                            QuickMarkStatusOption(code, label, selectedStatus == code, { selectedStatus = code }, Modifier.weight(1f))
                        }
                    }
                }
                AppDialogFieldColumn(label = "Student Name or Roll No.", modifier = Modifier.weight(1f)) {
                    if (selectedClass == null) {
                        AppDialogTextField(
                            value = "",
                            onValueChange = {},
                            placeholder = "Select a class first",
                            colors = AppDialogDefaultColors,
                            readOnly = true,
                            modifier = Modifier.fillMaxWidth(),
                            fieldHeight = 60.dp,
                            fontSize = 16.sp
                        )
                    } else {
                        AppDialogSearchSelectField(
                            value = selectedStudent?.let { "${it.rollNumber} — ${it.studentName}" }.orEmpty(),
                            placeholder = "Select student",
                            query = studentPickerQuery,
                            expanded = studentPickerExpanded,
                            options = studentOptions,
                            optionLabel = { student -> "${student.rollNumber} — ${student.studentName}" },
                            onQueryChange = { studentPickerQuery = it },
                            onToggleExpanded = {
                                studentPickerExpanded = !studentPickerExpanded
                                if (studentPickerExpanded) studentPickerQuery = ""
                            },
                            onDismissExpanded = {
                                studentPickerExpanded = false
                                studentPickerQuery = ""
                            },
                            onSelected = { student -> selectedStudentKey = student.key },
                            colors = AppDialogDefaultColors,
                            searchPlaceholder = "Search by name or roll number",
                            emptyLabel = "No students found",
                            modifier = Modifier.fillMaxWidth()
                        )
                    }
                }
                androidx.compose.material3.OutlinedButton(
                    onClick = { selectedStudent?.let(::addStudent) },
                    enabled = selectedStudent != null,
                    modifier = Modifier.width(104.dp).height(60.dp),
                    shape = RoundedCornerShape(8.dp),
                    border = BorderStroke(1.dp, AppPrimary)
                ) {
                    Icon(painterResource(Res.drawable.plus), null, Modifier.size(18.dp), tint = AppPrimary)
                    Spacer(Modifier.width(8.dp))
                    Text("Add", color = AppPrimary, fontSize = 14.sp, fontWeight = FontWeight.SemiBold)
                }
            }
            if (isRosterLoading) {
                Text("Loading the full class roster…", color = Color(0xFF6B7280), fontSize = 13.sp)
            }
            if (isAttendanceRecordsLoading && !isRosterLoading) {
                Text("Loading existing attendance records…", color = Color(0xFF6B7280), fontSize = 13.sp)
            }

            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(14.dp), verticalAlignment = Alignment.CenterVertically) {
                Row(Modifier.weight(1f).background(AppInfoBg, RoundedCornerShape(8.dp)).border(1.dp, AppInfoBorder, RoundedCornerShape(8.dp)).padding(horizontal = 14.dp, vertical = 10.dp), verticalAlignment = Alignment.CenterVertically) {
                    Icon(painterResource(Res.drawable.info_circle), null, Modifier.size(20.dp), tint = AppInfoText)
                    Spacer(Modifier.width(10.dp))
                    Text("Existing absence records for this class and date are listed below. Students not listed will be marked Present by default.", color = AppNavy, fontSize = 13.sp, fontWeight = FontWeight.SemiBold)
                }
                androidx.compose.material3.TextButton(onClick = { entries = emptyMap() }) {
                    Icon(painterResource(Res.drawable.trash), null, Modifier.size(18.dp), tint = AppDanger)
                    Spacer(Modifier.width(8.dp))
                    Text("Clear All", color = AppDanger, fontSize = 14.sp, fontWeight = FontWeight.SemiBold)
                }
            }

            QuickMarkTable(entries = entries, onStatusChanged = { key, status -> entries = entries + (key to entries.getValue(key).copy(status = status)) }, onRemarkChanged = { key, remark -> entries = entries + (key to entries.getValue(key).copy(remark = remark)) }, onRemove = { key -> entries = entries - key })
        }
        androidx.compose.material3.HorizontalDivider(color = AppBorder)
        AppDialogFooter(
            actions = listOf(
                AppDialogFooterAction("Cancel", onDismiss, AppDialogActionStyle.Secondary, enabled = !isSaving),
                AppDialogFooterAction(
                    if (isSaving) "Saving..." else "Save",
                    {
                        hasSubmitted = true
                        val currentDateError = attendanceDateValidationError(selectedDate, currentAppDateLabel())
                        selectedClass?.let { option ->
                            if (currentDateError.isBlank()) onSave(
                                QuickMarkSubmission(
                                    classSectionId = option.id,
                                    attendanceDate = selectedDate,
                                    exceptions = entries.values.map { entry ->
                                        QuickMarkExceptionUi(
                                            studentId = entry.student.studentId,
                                            status = entry.status.toApiAttendanceStatus(),
                                            remarks = entry.remark.trim().takeIf { it.isNotBlank() }
                                        )
                                    }
                                )
                            )
                        }
                    },
                    AppDialogActionStyle.Primary,
                    enabled = !isSaving && !isRosterLoading && !isAttendanceRecordsLoading && rosterErrorMessage.isBlank()
                )
            ),
            contentPadding = androidx.compose.foundation.layout.PaddingValues(horizontal = 28.dp),
            footerHeight = 84.dp
        )
    }
}

@Composable
private fun QuickMarkClassDropdown(
    value: String,
    options: List<AttendanceClassOption>,
    onValueChange: (AttendanceClassOption) -> Unit,
    modifier: Modifier,
    error: String = ""
) {
    var expanded by remember { mutableStateOf(false) }
    AppDialogFieldColumn(label = "Class", required = true, error = error, modifier = modifier) {
        BoxWithConstraints {
            OutlinedButton(
                onClick = { expanded = true },
                modifier = Modifier.fillMaxWidth().height(60.dp),
                shape = RoundedCornerShape(8.dp),
                border = BorderStroke(1.dp, if (error.isNotBlank()) AppDialogDefaultColors.danger else AppDialogDefaultColors.inputBorder)
            ) {
                Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
                    Text(
                        value.ifBlank { "Select class" },
                        color = if (value.isBlank()) AppDialogDefaultColors.muted else AppNavy,
                        fontSize = 16.sp,
                        fontWeight = FontWeight.Medium
                    )
                    Icon(painterResource(Res.drawable.chevron_down), "Open class options", Modifier.size(20.dp), tint = AppNavy)
                }
            }
            AppDropdownMenu(
                expanded = expanded,
                onDismissRequest = { expanded = false },
                width = maxWidth,
                containerColor = Color.White,
                border = BorderStroke(1.dp, AppBorder)
            ) {
                options.forEach { option ->
                    DropdownMenuItem(
                        text = { Text(option.label, color = AppNavy) },
                        onClick = { expanded = false; onValueChange(option) }
                    )
                }
            }
        }
    }
}

@Composable
private fun QuickMarkStatCard(title: String, value: String, accent: Color, background: Color, modifier: Modifier) {
    Column(modifier = modifier.background(background.copy(alpha = .45f), RoundedCornerShape(8.dp)).border(1.dp, accent.copy(alpha = .18f), RoundedCornerShape(8.dp)).padding(vertical = 10.dp, horizontal = 8.dp), horizontalAlignment = Alignment.CenterHorizontally) {
        Text(title, color = AppNavy, fontSize = 12.sp, fontWeight = FontWeight.ExtraBold, maxLines = 1)
        Spacer(Modifier.height(3.dp))
        Text(value, color = accent, fontSize = 24.sp, fontWeight = FontWeight.ExtraBold)
    }
}

@Composable
private fun QuickMarkStatusOption(code: String, label: String, selected: Boolean, onClick: () -> Unit, modifier: Modifier) {
    val shortLabel = compactAttendanceStatusLabel(code, label)
    Row(modifier.fillMaxWidth().height(60.dp).border(1.dp, if (selected) AppPrimary else AppBorder, RoundedCornerShape(8.dp)).clickable(onClick = onClick).semantics { contentDescription = label }.padding(horizontal = 12.dp), verticalAlignment = Alignment.CenterVertically) {
        Icon(painterResource(if (selected) Res.drawable.radio_selected else Res.drawable.radio_unselected), null, Modifier.size(21.dp), tint = if (selected) AppPrimary else AppNavy)
        Spacer(Modifier.width(8.dp))
        Text(shortLabel, color = attendanceStatusColor(shortLabel), fontSize = 14.sp, fontWeight = FontWeight.SemiBold, maxLines = 1)
    }
}

private fun compactAttendanceStatusLabel(code: String, label: String): String {
    val normalizedCode = code.trim()
    val normalizedLabel = label.trim()
    return when {
        normalizedCode.length in 1..4 && normalizedCode.any { it.isLetter() } -> normalizedCode
        normalizedLabel.contains("morning", ignoreCase = true) -> "A-M"
        normalizedLabel.contains("afternoon", ignoreCase = true) -> "A-A"
        normalizedLabel.contains("late", ignoreCase = true) -> "L"
        normalizedLabel.contains("excuse", ignoreCase = true) -> "E"
        normalizedLabel.contains("absent", ignoreCase = true) -> "A"
        normalizedLabel.contains("present", ignoreCase = true) -> "P"
        else -> normalizedCode.ifBlank { normalizedLabel }
    }
}

@Composable
private fun QuickMarkTable(entries: Map<String, QuickMarkRow>, onStatusChanged: (String, String) -> Unit, onRemarkChanged: (String, String) -> Unit, onRemove: (String) -> Unit) {
    val listState = rememberLazyListState()
    var previousEntryCount by remember { mutableStateOf(entries.size) }
    LaunchedEffect(entries.size) {
        if (entries.size > previousEntryCount) {
            listState.animateScrollToItem(entries.size - 1)
        }
        previousEntryCount = entries.size
    }
    Column(Modifier.fillMaxWidth().border(1.dp, AppBorder, RoundedCornerShape(8.dp))) {
        Row(Modifier.fillMaxWidth().background(AppSecondaryHeader).padding(horizontal = 14.dp, vertical = 12.dp), verticalAlignment = Alignment.CenterVertically) {
            QuickMarkHeader("Roll No.", .7f); QuickMarkHeader("Student Name", 1.35f); QuickMarkHeader("Status", .8f); QuickMarkHeader("Remarks", 1.8f); QuickMarkHeader("Remove", .55f)
        }
        DesktopAwareLazyColumn(
            modifier = Modifier.fillMaxWidth().height(260.dp),
            state = listState,
            // Keep the scrollbar thumb and drag target inside the table border.
            scrollbarEndOffset = (-8).dp
        ) {
            items(entries.values.sortedWith(compareBySmartText { it.student.rollNumber }), key = { it.student.key }) { entry ->
                Row(Modifier.fillMaxWidth().padding(horizontal = 14.dp, vertical = 6.dp), verticalAlignment = Alignment.CenterVertically) {
                    QuickMarkCell(entry.student.rollNumber, .7f)
                    QuickMarkCell(entry.student.studentName, 1.35f, bold = true)
                    QuickMarkStatusMenu(entry.status, { onStatusChanged(entry.student.key, it) }, Modifier.weight(.8f))
                    AppDialogTextField(value = entry.remark, onValueChange = { onRemarkChanged(entry.student.key, it) }, placeholder = "Add remarks", modifier = Modifier.weight(1.8f).padding(horizontal = 6.dp), colors = AppDialogDefaultColors, fieldHeight = 42.dp)
                    Box(Modifier.weight(.55f).clickable { onRemove(entry.student.key).also { } }, contentAlignment = Alignment.Center) { Icon(painterResource(Res.drawable.circle_x), "Remove ${entry.student.studentName}", Modifier.size(22.dp), tint = AppNavy) }
                }
            }
        }
    }
}

private val AppSecondaryHeader = Color(0xFFF8FAFD)

@Composable private fun RowScope.QuickMarkHeader(text: String, weight: Float) { Text(text, color = AppNavy, fontSize = 13.sp, fontWeight = FontWeight.ExtraBold, modifier = Modifier.weight(weight).padding(horizontal = 6.dp)) }
@Composable private fun RowScope.QuickMarkCell(text: String, weight: Float, bold: Boolean = false) { Text(text, color = AppNavy, fontSize = 13.sp, fontWeight = if (bold) FontWeight.Bold else FontWeight.SemiBold, modifier = Modifier.weight(weight).padding(horizontal = 6.dp), maxLines = 1) }

@Composable
private fun QuickMarkStatusMenu(value: String, onSelected: (String) -> Unit, modifier: Modifier) {
    var expanded by remember { mutableStateOf(false) }
    Box(modifier.clickable { expanded = true }.padding(horizontal = 6.dp), contentAlignment = Alignment.CenterStart) {
        Text(value, color = attendanceStatusColor(value), fontSize = 13.sp, fontWeight = FontWeight.SemiBold, modifier = Modifier.padding(horizontal = 6.dp, vertical = 8.dp))
        com.schoolconnect.app.designsystem.AppDropdownMenu(expanded, { expanded = false }, containerColor = Color.White) {
            com.schoolconnect.app.repository.systemlookup.SystemLookupOptions.activeItems("ATTENDANCE_STATUS")
                .filter { it.isQuickMarkStatusAllowed() }
                .forEach { item ->
                val status = item.code.orEmpty()
                androidx.compose.material3.DropdownMenuItem(text = { Text(status, color = attendanceStatusColor(status)) }, onClick = { expanded = false; onSelected(status) })
            }
        }
    }
}

private fun String.toApiAttendanceStatus(): String = when (this) {
    else -> com.schoolconnect.app.repository.systemlookup.SystemLookupOptions.serverCode("ATTENDANCE_STATUS", trim())
}
